import { Alert, Box, Checkbox, Chip, CircularProgress, FormControl, IconButton, InputAdornment, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { EntityType, EntityTypeCategory } from '../../core/types/data-models';

// All EntityTypeCategory values, in the order they should list in the filter
// and on each row — mirrors backend/src/data-models/entity/entity-type.ts.
const ALL_CATEGORIES: EntityTypeCategory[] = [
  'MARKETING',
  'SALES',
  'PRODUCT',
  'UX_DESIGN',
  'BUSINESS_STRATEGY',
  'MARKET_ANALYSIS',
  'FINANCE',
  'LEGAL',
  'RISK',
  'SYSTEM_DESIGN',
];

const CATEGORY_LABELS: Record<EntityTypeCategory, string> = {
  MARKETING: 'Marketing',
  SALES: 'Sales',
  PRODUCT: 'Product',
  UX_DESIGN: 'UX Design',
  BUSINESS_STRATEGY: 'Business Strategy',
  MARKET_ANALYSIS: 'Market Analysis',
  FINANCE: 'Finance',
  LEGAL: 'Legal',
  RISK: 'Risk',
  SYSTEM_DESIGN: 'System Design',
};

// ─── Details / edit dialog ─────────────────────────────────────────────────────

interface EntityTypeDialogProps {
  open: boolean;
  entityType: EntityType | null;
  /** Every other catalog entry, for the parent entity type picker's options. */
  allEntityTypes: EntityType[];
  onClose: () => void;
}

function EntityTypeDialog({ open, entityType, allEntityTypes, onClose }: EntityTypeDialogProps) {
  const qc = useQueryClient();
  const [description, setDescription] = useState('');
  const [llmPrompt, setLlmPrompt] = useState('');
  const [llmPromptMode, setLlmPromptMode] = useState<'markdown' | 'preview'>('markdown');
  const [categories, setCategories] = useState<EntityTypeCategory[]>([]);
  const [parentEntityType, setParentEntityType] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDescription(entityType?.description ?? '');
      setLlmPrompt(entityType?.llmPrompt ?? '');
      setLlmPromptMode('markdown');
      setCategories(entityType?.categories ?? []);
      setParentEntityType(entityType?.parentEntityType ?? '');
      setError(null);
    }
  }, [open, entityType]);

  // A type can't be its own parent, and can't group under one of its own
  // children (that would form a cycle) — offer neither as an option.
  const parentOptions = useMemo(
    () => allEntityTypes
      .filter((t) => t.name !== entityType?.name && t.parentEntityType !== entityType?.name)
      .map((t) => t.name)
      .sort(),
    [allEntityTypes, entityType],
  );

  const save = useMutation({
    mutationFn: () => {
      if (!entityType) throw new Error('No entity type selected');
      return resources.entityTypes.update(entityType.id, {
        description: description.trim() || null,
        llmPrompt: llmPrompt.trim() || null,
        categories,
        parentEntityType: parentEntityType || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entityTypes'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  if (!entityType) return null;

  return (
    <FormDialog
      open={open}
      title={entityType.name}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="md"
      submitLabel="Save"
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            autoFocus
          />
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="entity-type-dialog-category-label">Category</InputLabel>
              <Select
                labelId="entity-type-dialog-category-label"
                multiple
                value={categories}
                onChange={(e) => setCategories(
                  typeof e.target.value === 'string' ? (e.target.value.split(',') as EntityTypeCategory[]) : e.target.value,
                )}
                input={<OutlinedInput label="Category" />}
                renderValue={(sel) => (sel as EntityTypeCategory[]).map((c) => CATEGORY_LABELS[c]).join(', ')}
              >
                {ALL_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    <Checkbox checked={categories.includes(c)} size="small" />
                    <ListItemText primary={CATEGORY_LABELS[c]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="entity-type-dialog-parent-label">Parent entity type</InputLabel>
              <Select
                labelId="entity-type-dialog-parent-label"
                value={parentEntityType}
                onChange={(e) => setParentEntityType(e.target.value)}
                input={<OutlinedInput label="Parent entity type" />}
                displayEmpty
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {parentOptions.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                LLM Prompt
              </Typography>
              <ToggleButtonGroup
                value={llmPromptMode}
                exclusive
                size="small"
                onChange={(_e, val) => { if (val) setLlmPromptMode(val); }}
              >
                <ToggleButton value="markdown">Markdown</ToggleButton>
                <ToggleButton value="preview">Preview</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {llmPromptMode === 'markdown' ? (
              <TextField
                fullWidth
                value={llmPrompt}
                onChange={(e) => setLlmPrompt(e.target.value)}
                multiline
                rows={8}
                helperText="Prompt used by the LLM agent when extracting this entity type."
              />
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: 200,
                  '& p': { mt: 0, mb: 1 },
                  '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
                  '& pre': { bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto' },
                  '& code': { fontSize: 12, fontFamily: 'monospace' },
                }}
              >
                {llmPrompt
                  ? <ReactMarkdown>{llmPrompt}</ReactMarkdown>
                  : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Nothing to preview.</Typography>
                }
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EntityTypesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['entityTypes'],
    queryFn: () => resources.entityTypes.list({ page: 1, pageSize: 200, sort: 'name' }),
  });
  // `categories` defaults to [] on the backend, but a not-yet-restarted API
  // instance can still omit it — fall back here so the list never crashes.
  const entityTypes: EntityType[] = (data?.data ?? []).map((et) => ({ ...et, categories: et.categories ?? [] }));

  const [selected, setSelected] = useState<EntityType | null>(null);
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [hasPromptOnly, setHasPromptOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<EntityTypeCategory[]>([]);
  const [parentFilter, setParentFilter] = useState<string>('');

  // Parent entity types actually in use, for the filter dropdown's options.
  const parentOptions = useMemo(
    () => [...new Set(entityTypes.map((et) => et.parentEntityType).filter((p): p is string => !!p))].sort(),
    [entityTypes],
  );

  const visibleEntityTypes = useMemo(() => {
    const query = search.trim().toLowerCase();
    return entityTypes
      .filter((et) => !query || et.name.toLowerCase().includes(query))
      .filter((et) => !hasPromptOnly || !!et.llmPrompt)
      .filter((et) => categoryFilter.length === 0 || et.categories.some((c) => categoryFilter.includes(c)))
      .filter((et) => !parentFilter || et.parentEntityType === parentFilter)
      .sort((a, b) => (sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)));
  }, [entityTypes, search, sortDir, hasPromptOnly, categoryFilter, parentFilter]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error">{extractErrorMessage(error)}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="Entity Types"
        description="Global catalog of entity types. Open one to review its details and set the LLM prompt used when extracting it."
      />

      {entityTypes.length === 0 ? (
        <Alert severity="info">No entity types defined yet.</Alert>
      ) : (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={sortDir === 'asc' ? 'Sorted A → Z' : 'Sorted Z → A'}>
              <IconButton size="small" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
                {sortDir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Chip
              label="Has LLM prompt"
              size="small"
              clickable
              variant={hasPromptOnly ? 'filled' : 'outlined'}
              color={hasPromptOnly ? 'primary' : 'default'}
              onClick={() => setHasPromptOnly((v) => !v)}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="entity-type-category-filter-label">Category</InputLabel>
              <Select
                labelId="entity-type-category-filter-label"
                multiple
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(
                  typeof e.target.value === 'string' ? (e.target.value.split(',') as EntityTypeCategory[]) : e.target.value,
                )}
                input={<OutlinedInput label="Category" />}
                renderValue={(sel) => (sel as EntityTypeCategory[]).map((c) => CATEGORY_LABELS[c]).join(', ')}
              >
                {ALL_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    <Checkbox checked={categoryFilter.includes(c)} size="small" />
                    <ListItemText primary={CATEGORY_LABELS[c]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="entity-type-parent-filter-label">Parent entity type</InputLabel>
              <Select
                labelId="entity-type-parent-filter-label"
                value={parentFilter}
                onChange={(e) => setParentFilter(e.target.value)}
                input={<OutlinedInput label="Parent entity type" />}
                displayEmpty
              >
                <MenuItem value="">All</MenuItem>
                {parentOptions.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {visibleEntityTypes.length === 0 ? (
            <Alert severity="info">No entity types match the current filters.</Alert>
          ) : (
            visibleEntityTypes.map((et) => (
              <Box
                key={et.id}
                onClick={() => setSelected(et)}
                sx={{
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1,
                  px: 2,
                  py: 1.25,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600}>{et.name}</Typography>
                      {et.parentEntityType && (
                        <Typography variant="caption" color="text.secondary">↳ under {et.parentEntityType}</Typography>
                      )}
                    </Stack>
                    {et.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{et.description}</Typography>
                    )}
                    {et.categories.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
                        {et.categories.map((c) => (
                          <Chip key={c} label={CATEGORY_LABELS[c]} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    )}
                  </Box>
                  <Chip
                    label={et.llmPrompt ? 'Prompt set' : 'No prompt'}
                    size="small"
                    variant={et.llmPrompt ? 'filled' : 'outlined'}
                    color={et.llmPrompt ? 'primary' : 'default'}
                  />
                  <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
                </Stack>
              </Box>
            ))
          )}
        </Box>
      )}

      <EntityTypeDialog open={selected !== null} entityType={selected} allEntityTypes={entityTypes} onClose={() => setSelected(null)} />
    </Box>
  );
}
